import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  MapPinIcon,
  UserIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useToast, ToastContainer } from '../components/common/Toast';
import ConfigNavigation from '../components/ConfigNavigation';

interface SiteAssignment {
  id: string;
  user_id: string;
  site_id: string;
  role_id: string;
  site: {
    id: string;
    code: string;
    name: string;
  };
  role: {
    id: string;
    name: string;
    label?: string;
    description?: string;
  };
  created_at: string;
  is_simulated?: boolean;
}

interface SessionConfig {
  siteId: number;
  maxSessions: number;
  sessionTimeout: number; // en minutes
  sessionType: 'permanent' | 'temporaire'; // Type de session
  allowedHours: {
    start: string;
    end: string;
  };
  permissions: {
    read: boolean;
    write: boolean;
    delete: boolean;
    admin: boolean;
  };
  restrictions: {
    ipWhitelist: string[];
    deviceLimit: number;
    concurrentSessions: boolean;
  };
}

const UserSessionConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, showSuccess, showError } = useToast();
  
  const [user, setUser] = useState<any>(null);
  const [siteAssignments, setSiteAssignments] = useState<SiteAssignment[]>([]);
  const [sessionConfigs, setSessionConfigs] = useState<SessionConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isSendingWelcome, setIsSendingWelcome] = useState(false);

  useEffect(() => {
    // Récupérer les données de l'utilisateur depuis l'état de navigation
    if (location.state?.user) {
      setUser(location.state.user);
    }
    
    // Récupérer les assignations de sites depuis l'état de navigation
    if (location.state?.siteAssignments) {
      setSiteAssignments(location.state.siteAssignments);
      initializeSessionConfigs(location.state.siteAssignments);
    }
    
    setIsLoading(false);
  }, [location.state]);

  const initializeSessionConfigs = (assignments: SiteAssignment[]) => {
    const configs: SessionConfig[] = assignments.map(assignment => ({
      siteId: parseInt(assignment.site_id),
      maxSessions: 3,
      sessionTimeout: 480, // 8 heures par défaut
      sessionType: 'temporaire', // Par défaut temporaire
      allowedHours: {
        start: '08:00',
        end: '18:00'
      },
      permissions: {
        read: true,
        write: assignment.role.name === 'ADMINISTRATEUR' || assignment.role.name === 'SUPERVISEUR',
        delete: assignment.role.name === 'ADMINISTRATEUR',
        admin: assignment.role.name === 'ADMINISTRATEUR'
      },
      restrictions: {
        ipWhitelist: [],
        deviceLimit: 2,
        concurrentSessions: true
      }
    }));
    
    setSessionConfigs(configs);
  };

  const updateSessionConfig = (siteId: number, updates: Partial<SessionConfig>) => {
    setSessionConfigs(prev => prev.map(config => 
      config.siteId === siteId ? { ...config, ...updates } : config
    ));
  };

  const updatePermissions = (siteId: number, permission: keyof SessionConfig['permissions'], value: boolean) => {
    updateSessionConfig(siteId, {
      permissions: {
        ...sessionConfigs.find(c => c.siteId === siteId)!.permissions,
        [permission]: value
      }
    });
  };

  const updateRestrictions = (siteId: number, restriction: keyof SessionConfig['restrictions'], value: any) => {
    updateSessionConfig(siteId, {
      restrictions: {
        ...sessionConfigs.find(c => c.siteId === siteId)!.restrictions,
        [restriction]: value
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Simuler la sauvegarde des configurations
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showSuccess('Configuration des sessions sauvegardée avec succès !');
      
      // Rediriger vers la liste des utilisateurs
      setTimeout(() => {
        navigate('/administration/users');
      }, 1500);
      
    } catch (error) {
      showError('Erreur lors de la sauvegarde de la configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    setShowWelcomeModal(true);
  };

  const handleSendWelcomeEmail = async () => {
    setIsSendingWelcome(true);
    
    try {
      // Simuler l'envoi d'un email de bienvenue
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      showSuccess('Email de bienvenue envoyé avec succès !');
      
      // Rediriger vers la liste des utilisateurs après un délai
      setTimeout(() => {
        navigate('/administration/users');
      }, 2000);
      
    } catch (error) {
      showError('Erreur lors de l\'envoi de l\'email de bienvenue');
    } finally {
      setIsSendingWelcome(false);
      setShowWelcomeModal(false);
    }
  };

  const handleSkipWelcome = () => {
    setShowWelcomeModal(false);
    navigate('/administration/users');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user || !siteAssignments.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Données manquantes</h2>
          <p className="text-gray-600 mb-4">Impossible de charger les informations de l'utilisateur</p>
          <button
            onClick={() => navigate('/administration/users')}
            className="btn-primary"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <ConfigNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/administration/users')}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Configuration des Sessions</h1>
                  <p className="text-sm text-gray-600">
                    Utilisateur : {user.name} (@{user.username})
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSkip}
                  className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ArrowLeftIcon className="h-4 w-4 rotate-180" />
                  <span>Étape suivante</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center space-x-2 px-6 py-3 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:ring-4 focus:ring-orange-300 disabled:bg-orange-400 disabled:cursor-not-allowed rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                >
                  <CheckIcon className="h-5 w-5" />
                  <span>{isSaving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="py-8">
          {/* Success Message */}
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-green-800">
                  Utilisateur créé avec succès ! 🎉
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  Configurez maintenant les sessions et permissions pour chaque site assigné
                </p>
              </div>
            </div>
          </div>

          {/* Site Assignments */}
          <div className="grid gap-6">
            {siteAssignments.map((site) => {
              const config = sessionConfigs.find(c => c.siteId === parseInt(site.site_id));
              if (!config) return null;

              return (
                <div key={site.id} className="bg-white rounded-xl shadow-sm border">
                  {/* Site Header */}
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <MapPinIcon className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{site.site.name}</h3>
                          <p className="text-sm text-gray-600">{site.site.code}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          site.role.name === 'ADMINISTRATEUR' ? 'bg-purple-100 text-purple-800' :
                          site.role.name === 'SUPERVISEUR' ? 'bg-blue-100 text-blue-800' :
                          site.role.name === 'OPERATEUR' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {site.role.label || site.role.name}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          config.sessionType === 'permanent' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {config.sessionType === 'permanent' ? '🔒 Permanent' : '⏰ Temporaire'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Configuration Options */}
                  <div className="p-6 space-y-6">
                    {/* Sessions */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <ClockIcon className="h-4 w-4 mr-2" />
                        Configuration des Sessions
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Type de session
                          </label>
                          <select
                            value={config.sessionType}
                            onChange={(e) => {
                              const newType = e.target.value as 'permanent' | 'temporaire';
                              const updates: Partial<SessionConfig> = { sessionType: newType };
                              
                              // Si on passe à permanent, désactiver le timeout
                              if (newType === 'permanent') {
                                updates.sessionTimeout = 0;
                              } else if (newType === 'temporaire' && config.sessionTimeout === 0) {
                                // Si on repasse à temporaire et que le timeout était à 0, remettre une valeur par défaut
                                updates.sessionTimeout = 480; // 8 heures par défaut
                              }
                              
                              updateSessionConfig(parseInt(site.site_id), updates);
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="temporaire">Temporaire</option>
                            <option value="permanent">Permanent</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {config.sessionType === 'permanent' 
                              ? 'Session sans expiration, accessible en permanence' 
                              : 'Session avec timeout configurable, expire après inactivité'
                            }
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Sessions simultanées max
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={config.maxSessions}
                            onChange={(e) => updateSessionConfig(parseInt(site.site_id), { maxSessions: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Timeout session (min)
                            {config.sessionType === 'permanent' && (
                              <span className="text-xs text-gray-500 ml-1">(désactivé pour session permanente)</span>
                            )}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="1440"
                            step="30"
                            value={config.sessionType === 'permanent' ? 0 : config.sessionTimeout}
                            onChange={(e) => updateSessionConfig(parseInt(site.site_id), { sessionTimeout: parseInt(e.target.value) })}
                            disabled={config.sessionType === 'permanent'}
                            className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                              config.sessionType === 'permanent' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Limite d'appareils
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={config.restrictions.deviceLimit}
                            onChange={(e) => updateRestrictions(parseInt(site.site_id), 'deviceLimit', parseInt(e.target.value))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Horaires */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-4">Horaires d'Accès</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Heure de début
                          </label>
                          <input
                            type="time"
                            value={config.allowedHours.start}
                            onChange={(e) => updateSessionConfig(parseInt(site.site_id), { 
                              allowedHours: { ...config.allowedHours, start: e.target.value }
                            })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Heure de fin
                          </label>
                          <input
                            type="time"
                            value={config.allowedHours.end}
                            onChange={(e) => updateSessionConfig(parseInt(site.site_id), { 
                              allowedHours: { ...config.allowedHours, end: e.target.value }
                            })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Permissions */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <ShieldCheckIcon className="h-4 w-4 mr-2" />
                        Permissions
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(config.permissions).map(([permission, value]) => (
                          <label key={permission} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => updatePermissions(parseInt(site.site_id), permission as keyof SessionConfig['permissions'], e.target.checked)}
                              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700 capitalize">{permission}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Restrictions */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-4">Restrictions</h4>
                      <div className="space-y-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={config.restrictions.concurrentSessions}
                            onChange={(e) => updateRestrictions(parseInt(site.site_id), 'concurrentSessions', e.target.checked)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700">Autoriser les sessions simultanées</span>
                        </label>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            IPs autorisées (une par ligne)
                          </label>
                          <textarea
                            value={config.restrictions.ipWhitelist.join('\n')}
                            onChange={(e) => updateRestrictions(parseInt(site.site_id), 'ipWhitelist', e.target.value.split('\n').filter(ip => ip.trim()))}
                            placeholder="192.168.1.1&#10;10.0.0.1"
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Laissez vide pour autoriser toutes les IPs</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-8 bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Résumé de la Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{siteAssignments.length}</div>
                <div className="text-sm text-blue-700">Sites configurés</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {sessionConfigs.filter(c => c.permissions.admin).length}
                </div>
                <div className="text-sm text-green-700">Sites avec admin</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {sessionConfigs.reduce((sum, c) => sum + c.maxSessions, 0)}
                </div>
                <div className="text-sm text-orange-700">Sessions totales</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {sessionConfigs.filter(c => c.sessionType === 'permanent').length}
                </div>
                <div className="text-sm text-purple-700">Sessions permanentes</div>
              </div>
            </div>
          </div>
        </div>

        <ToastContainer toasts={toasts} position="top-right" />

        {/* Modal de bienvenue */}
        {showWelcomeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserIcon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Envoyer un email de bienvenue ?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Envoyer un email de bienvenue à <strong>{user?.name}</strong> avec ses informations de connexion et les détails de ses sites assignés.
                </p>
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleSkipWelcome}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Passer
                  </button>
                  <button
                    onClick={handleSendWelcomeEmail}
                    disabled={isSendingWelcome}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {isSendingWelcome ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Envoi...</span>
                      </div>
                    ) : (
                      'Envoyer l\'email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSessionConfigPage;
